import { Builder } from '@builder.io/react';
import { BuilderComponentState } from '@builder.io/react/dist/types/src/components/builder-component.component';
import classNames from 'classnames';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SwiperCore from 'swiper';
import { EffectFade } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types/swiper-options';

import { useInView } from 'react-intersection-observer';
import { useWindowSize } from '../../hook/useWindowSize';
import { BusinessIndustries } from '../../types/BusinessIndustries';
import BusinessHubTrustedBrand from '../business-hub-trusted-brand/business-hub-trusted-brand';
import { BusinessHubIndustriesGridBuilderConfig } from './business-hub-industries-grid.builder-component';
import styles from './business-hub-industries-grid.module.scss';
import { IndustriesItem } from './industries-item';

const options: SwiperOptions = {
    slidesPerView: 'auto',
    spaceBetween: 10,
    allowTouchMove: true,
    breakpoints: {
        992: {
            spaceBetween: 20,
        },
    },
};

interface SSProps {
    builderState?: BuilderComponentState;
}

export interface BusinessHubIndustriesGrid extends SSProps {
    industries?: BusinessIndustries[];
    showJustedBrands?: boolean;
    autoplay?: boolean;
    showTitle?: boolean;
}

const BusinessHubIndustriesGrid: FC<BusinessHubIndustriesGrid> = (props) => {
    const { industries: industriesProps, builderState, showJustedBrands, autoplay, showTitle } = props;
    const config = builderState?.state[BusinessHubIndustriesGridBuilderConfig.name];

    const { width = 0 } = useWindowSize();
    const [displayIndustries, setDisplayIndustries] = useState<string[]>([]);
    const [pagination, setPagination] = useState<{ page: number; size: number }>({ page: 0, size: 4 });
    const [selectedIndex, setSelectedIndex] = useState<number | undefined>(autoplay ? -1 : undefined);
    const intervalRef = useRef<NodeJS.Timer>();
    const wordsRef = useRef<HTMLDivElement>(null);
    const [trustedBrandSwiper, setTrustedBrandSwiper] = useState<SwiperCore>();
    const { ref, inView } = useInView({ triggerOnce: true });

    const industries =
        useMemo(() => {
            if (Builder.isEditing || Builder.isPreviewing) {
                return industriesProps || (config?.industries as BusinessIndustries[]);
            }

            return industriesProps;
        }, [industriesProps, config]) || [];

    const _getNextPage = () => {
        if (autoplay) {
            setDisplayIndustries(industries.map((i) => i.name));
        } else {
            const nextItems = [...industries].slice(pagination.size * pagination.page, pagination.size * (pagination.page + 1)).map((i) => i.name);
            setDisplayIndustries([...displayIndustries, ...nextItems]);
            setPagination((p) => ({
                ...p,
                page: p.page + 1,
            }));
        }
    };

    useEffect(() => {
        _getNextPage();
    }, []);

    const _industryClick = (index: number) => {
        if (!autoplay && index === selectedIndex) {
            setSelectedIndex(undefined);
        } else {
            setSelectedIndex(index);
        }
    };

    const play = useCallback(() => {
        if (!autoplay) return;

        const interval = setInterval(() => {
            setSelectedIndex((val) => {
                let index = (val || 0) + 1;

                if (index >= industries.length) {
                    index = -1;
                }

                return index;
            });
        }, 4000);
        intervalRef.current = interval;
    }, [industries]);

    const pause = () => {
        if (!autoplay) return;
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
    };

    useEffect(() => {
        if (inView) play();

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [inView, play]);

    useEffect(() => {
        if (!showTitle) return;
        if (!wordsRef.current) return;
        if (!Number.isInteger(selectedIndex) || selectedIndex === undefined) return;

        const words = wordsRef.current.querySelectorAll(`span`);

        if (!words.length) return;

        const realIndex = selectedIndex + 1;

        words[realIndex].classList.remove(styles.isHidden);
        words[realIndex].classList.add(styles.isVisible);

        for (let index = 0; index < words.length; index++) {
            const element = words[index];
            if (index === realIndex) continue;

            element.classList.add(styles.isHidden);
            element.classList.remove(styles.isVisible);
        }

        if (trustedBrandSwiper?.realIndex !== realIndex - 1) {
            trustedBrandSwiper?.slideTo(realIndex - 1, 300);
        }
    }, [selectedIndex, wordsRef, showTitle]);

    return (
        <div ref={ref} className={styles.container} onMouseEnter={pause} onMouseLeave={play}>
            {showTitle ? (
                <div ref={wordsRef} className={styles.wordsContainer}>
                    <div>Built for&nbsp;</div>
                    <div className={styles.wordsWrapper}>
                        <span className={styles.isVisible}>your industry.</span>
                        {industries?.map(({ name }, index) => (
                            <span key={`${name}-index`}>{name?.toLowerCase()}.</span>
                        ))}
                    </div>
                </div>
            ) : null}
            <div className={styles.carouselContainer}>
                {width > 992 ? (
                    <Swiper {...options}>
                        {industries.map(({ name }, index) => (
                            <SwiperSlide
                                key={name}
                                className={classNames(styles.carouselItem, { [styles.active]: index === selectedIndex || (index === 0 && selectedIndex === -1) })}
                                onClick={() => _industryClick(index)}
                            >
                                {name}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                ) : (
                    <div className={styles.mobileContainer}>
                        <Swiper {...options}>
                            {industries.slice(0, industries.length / 2).map(({ name }, index) => (
                                <SwiperSlide
                                    key={name}
                                    className={classNames(styles.carouselItem, { [styles.active]: index === selectedIndex || (index === 0 && selectedIndex === -1) })}
                                    onClick={() => _industryClick(index)}
                                >
                                    {name}
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        <Swiper {...options}>
                            {industries.slice(industries.length / 2).map(({ name }, index) => (
                                <SwiperSlide
                                    key={name}
                                    className={classNames(styles.carouselItem, {
                                        [styles.active]: index + industries.length / 2 === selectedIndex || (index + industries.length / 2 === 0 && selectedIndex === -1),
                                    })}
                                    onClick={() => _industryClick(index + industries.length / 2)}
                                >
                                    {name}
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                )}
            </div>
            <div className={styles.industriesContainer}>
                {industries.map((industry, index) => {
                    let hide = Number.isInteger(selectedIndex) ? selectedIndex !== index : false;
                    if (index === 0 && selectedIndex === -1) {
                        hide = false;
                    }
                    if (!Number.isInteger(selectedIndex)) {
                        hide = !displayIndustries.includes(industry.name);
                    }

                    return <IndustriesItem key={industry.name} {...industry} reversed={!autoplay && index % 2 !== 0} hide={hide} showQuote={autoplay} />;
                })}
            </div>

            {showJustedBrands ? (
                <div className={styles.trustedBrandContainer}>
                    <div className={styles.trustedTitle}>Trusted by</div>
                    <Swiper
                        slidesPerView={1}
                        draggable={false}
                        noSwiping
                        allowTouchMove={false}
                        effect={'fade'}
                        fadeEffect={{ crossFade: true }}
                        // creativeEffect={{
                        //     prev: {
                        //         opacity: 0,
                        //     },
                        //     next: {
                        //         opacity: 1,
                        //     },
                        // }}
                        modules={[EffectFade]}
                        onInit={(swiper: SwiperCore) => {
                            setTrustedBrandSwiper(swiper);
                        }}
                    >
                        {industries.map((industry) => (
                            <SwiperSlide key={industry.name} className={styles.trustedBrandSlide}>
                                <BusinessHubTrustedBrand trustedBrands={industry.trustedBrands} className={styles.trustedBrand} />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            ) : null}

            {autoplay || displayIndustries.length === industries.length ? null : (
                <div className={styles.loadMore}>
                    <button className={styles.loadMoreButton} onClick={_getNextPage}>
                        Load more
                    </button>
                </div>
            )}
        </div>
    );
};

export default BusinessHubIndustriesGrid;
